import { useState, useRef, useEffect } from 'react';

export interface DesignElement {
  id: string;
  type: 'text' | 'image';
  content: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  fontWeight?: string;
  letterSpacing?: string;
  textTransform?: string;
  textAlign?: 'left' | 'center' | 'right';
  textStroke?: { width: number; color: string };
  zIndex: number;
}

interface DesignCanvasProps {
  shirtColor: string;
  shirtImage: string;
  elements: DesignElement[];
  selectedElementId: string | null;
  onElementUpdate: (id: string, updates: Partial<DesignElement>) => void;
  onElementSelect: (id: string | null) => void;
  printLocation: string;
}

const PRINT_BOUNDARIES = {
  'Front Center': { x: 25, y: 30, width: 50, height: 40 },
  'Back Center': { x: 25, y: 30, width: 50, height: 40 },
  'Left Chest': { x: 15, y: 20, width: 20, height: 20 },
  'Right Chest': { x: 65, y: 20, width: 20, height: 20 },
};

export function DesignCanvas({
  shirtColor,
  shirtImage,
  elements,
  selectedElementId,
  onElementUpdate,
  onElementSelect,
  printLocation,
}: DesignCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);

  // Resolve the current print boundary – fall back to “Front Center” if the key is missing.
  const boundary =
    PRINT_BOUNDARIES[printLocation as keyof typeof PRINT_BOUNDARIES] ??
    PRINT_BOUNDARIES['Front Center'];

  /**
   * Common mouse‑down handler for drag / resize / rotate actions.
   * Stops propagation so the canvas click handler does not deselect the element.
   */
  const handleMouseDown = (
    e: React.MouseEvent,
    elementId: string,
    action: 'drag' | 'resize' | 'rotate',
    handle?: string
  ) => {
    e.stopPropagation();
    onElementSelect(elementId);

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) {
      console.warn('Canvas rect not available – mouse down ignored.');
      return;
    }

    setDragStart({ x: e.clientX, y: e.clientY });

    if (action === 'drag') {
      setIsDragging(true);
    } else if (action === 'resize') {
      setIsResizing(true);
      setResizeHandle(handle ?? null);
    } else if (action === 'rotate') {
      setIsRotating(true);
    }
  };

  /**
   * Global mouse move / up listeners – attached only while an interaction is active.
   * All calculations are performed in percentage coordinates (0‑100) to stay resolution‑agnostic.
   */
  useEffect(() => {
    if (!isDragging && !isResizing && !isRotating) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!selectedElementId || !canvasRef.current) return;

      const element = elements.find((el) => el.id === selectedElementId);
      if (!element) return;

      const canvasRect = canvasRef.current.getBoundingClientRect();

      // Convert pixel movement to % of the canvas dimensions.
      const deltaX = ((e.clientX - dragStart.x) / canvasRect.width) * 100;
      const deltaY = ((e.clientY - dragStart.y) / canvasRect.height) * 100;

      // ----- Drag ---------------------------------------------------------
      if (isDragging) {
        let newX = element.x + deltaX;
        let newY = element.y + deltaY;

        // Constrain the element inside the printable area.
        newX = Math.max(
          boundary.x,
          Math.min(boundary.x + boundary.width - element.width, newX)
        );
        newY = Math.max(
          boundary.y,
          Math.min(boundary.y + boundary.height - element.height, newY)
        );

        onElementUpdate(selectedElementId, { x: newX, y: newY });
        setDragStart({ x: e.clientX, y: e.clientY });
        return;
      }

      // ----- Resize --------------------------------------------------------
      if (isResizing && resizeHandle) {
        let newWidth = element.width;
        let newHeight = element.height;
        let newX = element.x;
        let newY = element.y;

        // Horizontal adjustments
        if (resizeHandle.includes('e')) {
          newWidth = Math.max(5, element.width + deltaX);
        }
        if (resizeHandle.includes('w')) {
          newWidth = Math.max(5, element.width - deltaX);
          newX = element.x + deltaX;
        }

        // Vertical adjustments
        if (resizeHandle.includes('s')) {
          newHeight = Math.max(5, element.height + deltaY);
        }
        if (resizeHandle.includes('n')) {
          newHeight = Math.max(5, element.height - deltaY);
          newY = element.y + deltaY;
        }

        // Keep the resized element inside the print boundary.
        if (newX < boundary.x) {
          newWidth -= boundary.x - newX;
          newX = boundary.x;
        }
        if (newY < boundary.y) {
          newHeight -= boundary.y - newY;
          newY = boundary.y;
        }
        if (newX + newWidth > boundary.x + boundary.width) {
          newWidth = boundary.x + boundary.width - newX;
        }
        if (newY + newHeight > boundary.y + boundary.height) {
          newHeight = boundary.y + boundary.height - newY;
        }

        onElementUpdate(selectedElementId, {
          x: newX,
          y: newY,
          width: newWidth,
          height: newHeight,
        });
        setDragStart({ x: e.clientX, y: e.clientY });
        return;
      }

      // ----- Rotate --------------------------------------------------------
      if (isRotating) {
        const centerX =
          canvasRect.left +
          (element.x / 100) * canvasRect.width +
          (element.width / 100) * canvasRect.width * 0.5;
        const centerY =
          canvasRect.top +
          (element.y / 100) * canvasRect.height +
          (element.height / 100) * canvasRect.height * 0.5;

        const angleDeg =
          (Math.atan2(e.clientY - centerY, e.clientX - centerX) * 180) / Math.PI;

        onElementUpdate(selectedElementId, { rotation: angleDeg });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setIsRotating(false);
      setResizeHandle(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // Cleanup listeners when interaction ends or component unmounts.
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [
    isDragging,
    isResizing,
    isRotating,
    selectedElementId,
    elements,
    dragStart,
    boundary,
    resizeHandle,
    onElementUpdate,
  ]);

  /** Deselect element when clicking on empty canvas space. */
  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onElementSelect(null);
    }
  };

  return (
    <div className="relative w-full max-w-[420px] select-none">
      <div
        ref={canvasRef}
        onClick={handleCanvasClick}
        className="relative w-full h-[520px] rounded-2xl overflow-hidden bg-white shadow-xl border border-gray-100"
        style={{ cursor: isDragging ? 'grabbing' : 'default' }}
      >
        {/* Shirt mockup — rendered at full resolution, no filters */}
        <img
          src={shirtImage}
          alt={`${shirtColor} shirt`}
          className="absolute inset-0 w-full h-full object-contain object-center pointer-events-none"
          style={{
            imageRendering: 'auto',
            filter: 'none',
            WebkitFilter: 'none',
          }}
          draggable={false}
        />

        {/* Print boundary overlay */}
        <div
          className="absolute border-2 border-dashed border-blue-400/40 pointer-events-none"
          style={{
            left: `${boundary.x}%`,
            top: `${boundary.y}%`,
            width: `${boundary.width}%`,
            height: `${boundary.height}%`,
          }}
        >
          <div className="absolute -top-5 left-0 text-[10px] font-bold text-blue-500 bg-white/90 px-2 py-0.5 rounded">
            Print Area
          </div>
        </div>

        {/* Design elements */}
        {elements
          .slice()
          .sort((a, b) => a.zIndex - b.zIndex)
          .map((element) => {
            const isSelected = element.id === selectedElementId;

            return (
              <div
                key={element.id}
                className="absolute"
                style={{
                  left: `${element.x}%`,
                  top: `${element.y}%`,
                  width: `${element.width}%`,
                  height: `${element.height}%`,
                  transform: `rotate(${element.rotation}deg)`,
                  transformOrigin: 'center',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  zIndex: element.zIndex,
                }}
                onMouseDown={(e) => handleMouseDown(e, element.id, 'drag')}
              >
                {element.type === 'text' ? (
                  <div
                    className="w-full h-full flex items-center justify-center pointer-events-none"
                    style={{
                      fontSize: `${element.fontSize ?? 32}px`,
                      fontFamily: element.fontFamily ?? 'inherit',
                      color: element.color ?? '#000000',
                      fontWeight: element.fontWeight ?? 'bold',
                      letterSpacing: element.letterSpacing ?? 'normal',
                      textTransform: (element.textTransform as any) ?? 'none',
                      textAlign: element.textAlign ?? 'center',
                      lineHeight: 1.1,
                      wordBreak: 'break-word',
                      WebkitTextStroke: element.textStroke 
                        ? `${element.textStroke.width}px ${element.textStroke.color}` 
                        : 'none',
                      paintOrder: 'stroke fill',
                    }}
                  >
                    {element.content}
                  </div>
                ) : (
                  <img
                    src={element.content}
                    alt="Design"
                    className="w-full h-full object-contain pointer-events-none"
                    draggable={false}
                  />
                )}

                {/* Selection outline & resize/rotate handles */}
                {isSelected && (
                  <>
                    {/* Outline */}
                    <div className="absolute inset-0 border-2 border-blue-500 pointer-events-none rounded" />

                    {/* Resize handles */}
                    {['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'].map((handle) => {
                      const isCorner = handle.length === 2;
                      const positionStyles: Record<string, any> = {
                        nw: { top: -4, left: -4, cursor: 'nwse-resize' },
                        ne: { top: -4, right: -4, cursor: 'nesw-resize' },
                        sw: { bottom: -4, left: -4, cursor: 'nesw-resize' },
                        se: { bottom: -4, right: -4, cursor: 'nwse-resize' },
                        n: { top: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
                        s: { bottom: -4, left: '50%', transform: 'translateX(-50%)', cursor: 'ns-resize' },
                        e: { right: -4, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' },
                        w: { left: -4, top: '50%', transform: 'translateY(-50%)', cursor: 'ew-resize' },
                      };

                      return (
                        <div
                          key={handle}
                          className={`absolute ${isCorner ? 'w-3 h-3' : 'w-2 h-2'} bg-blue-500 border-2 border-white rounded-full`}
                          style={positionStyles[handle]}
                          onMouseDown={(e) => handleMouseDown(e, element.id, 'resize', handle)}
                        />
                      );
                    })}

                    {/* Rotation handle */}
                    <div
                      className="absolute -top-8 left-1/2 -translate-x-1/2 w-6 h-6 flex items-center justify-center bg-blue-500 rounded-full cursor-grab hover:bg-blue-600 transition-colors"
                      onMouseDown={(e) => handleMouseDown(e, element.id, 'rotate')}
                    >
                      <i className="ri-refresh-line text-white text-xs" />
                    </div>
                  </>
                )}
              </div>
            );
          })}

        {/* Live badge */}
        <div className="absolute top-3 left-3 bg-red-900 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 pointer-events-none">
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse inline-block" />
          Live Preview
        </div>

        {/* Print location badge */}
        <div className="absolute top-3 right-3 bg-white/90 text-gray-700 text-xs font-bold px-3 py-1 rounded-full border border-gray-200 pointer-events-none">
          {printLocation}
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-3 font-medium flex items-center justify-center gap-1">
        <i className="ri-drag-move-line" />
        Drag, resize, and rotate your design
      </p>
    </div>
  );
}
